import api from '../lib/api';
import type {
  User,
  UpdateProfileData,
  UpdatePasswordData,
  CreateUserData,
  UpdateUserData,
} from '../types';

export const userService = {
  // Get current user from API
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<{ data: User }>('/users/current');
    return response.data.data;
  },

  // Get user by ID
  getUser: async (id: number): Promise<User> => {
    const response = await api.get<{ data: User }>(`/users/${id}`);
    return response.data.data;
  },

  // Get all users (admin only)
  getAllUsers: async (): Promise<User[]> => {
    const response = await api.get<{ data: User[] }>('/users');
    return response.data.data;
  },

  // Update user profile
  updateProfile: async (id: number, data: UpdateProfileData): Promise<User> => {
    const response = await api.patch<{ data: User }>(`/users/${id}`, {
      user: data,
    });

    // Update localStorage with new user data
    const updatedUser = response.data.data;
    localStorage.setItem('user', JSON.stringify(updatedUser));

    return updatedUser;
  },

  // Update password
  updatePassword: async (id: number, data: UpdatePasswordData): Promise<{ message: string }> => {
    const response = await api.patch(`/users/${id}/update_password`, {
      user: data,
    });
    return response.data;
  },

  // Upload profile picture
  uploadProfilePicture: async (id: number, file: File): Promise<User> => {
    const formData = new FormData();
    formData.append('profile_picture', file);

    const response = await api.post<{ data: User }>(
      `/users/${id}/upload_profile_picture`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    // Update localStorage with new user data
    const updatedUser = response.data.data;
    localStorage.setItem('user', JSON.stringify(updatedUser));

    return updatedUser;
  },

  // Create user (admin only)
  createUser: async (data: CreateUserData): Promise<User> => {
    const response = await api.post<{ data: User }>('/users', {
      user: data,
    });
    return response.data.data;
  },

  // Toggle user active status (admin only)
  toggleActive: async (id: number): Promise<User> => {
    const response = await api.patch<{ data: User }>(`/users/${id}/toggle_active`);
    return response.data.data;
  },

  // Update user role (admin only)
  updateRole: async (id: number, role: 'regular_user' | 'account_admin'): Promise<User> => {
    const response = await api.patch<{ data: User }>(`/users/${id}/update_role`, {
      role,
    });
    return response.data.data;
  },

  // Update CRM-specific user fields like crm_role and division_id (admin only).
  // Uses the standard PATCH /users/:id endpoint with strong-params filtering on
  // the server side for non-admins.
  updateUserAdmin: async (id: number, data: UpdateUserData): Promise<User> => {
    const response = await api.patch<{ data: User }>(`/users/${id}`, { user: data });
    return response.data.data;
  },
};

export default userService;
